<template>
  <template v-if="!isMobile">
    <SFItemsSlider
      ref="productImageSlider"
      class="size-full"
      :with-arrows="areArrowsShown"
      tabindex="0"
      loop
      :slider-tabindex="0"
      @update:active-slide="(activeSlide) => (currentIndex = activeSlide)"
    >
      <template #prev-button="{ prev, canGoPrev }">
        <SFSliderArrowButton
          class="absolute top-[40%] z-50 bg-white hover:bg-white"
          :aria-label="$t('image_slider.a11ly.go_to_previous_image')"
          :disabled="!canGoPrev"
          direction="left"
          translate-on-hover
          @click.stop="prev()"
        />
      </template>
      <template #next-button="{ next, canGoNext }">
        <SFSliderArrowButton
          class="absolute top-[40%] z-50 bg-white hover:bg-white"
          :aria-label="$t('image_slider.a11ly.go_to_next_image')"
          :disabled="!canGoNext"
          direction="right"
          translate-on-hover
          @click.stop="next()"
        />
      </template>
      <SFLink
        v-for="(item, imageIndex) in images"
        :key="item.hash"
        :to="link"
        raw
        class="relative min-w-full snap-start snap-always"
        @click="handleClick"
      >
        <SFProductImage
          v-if="item"
          :image="item"
          :alt="
            $t('product_image.alt_with_image_index', {
              alt,
              index: imageIndex + 1,
              total: images.length,
            })
          "
          :image-loading="getImageLoading(imageIndex)"
          :preload="shouldPreload(imageIndex)"
          sizes="xs:50vw sm:50vw md:40vw lg:33vw xl:320px"
          class="absolute inset-0"
        />
      </SFLink>
    </SFItemsSlider>
    <SFSliderDotsIndicator
      v-if="isMobile"
      v-model:current-index="currentIndex"
      :count="images.length"
      class="absolute bottom-2 left-1/2 z-50 -translate-x-1/2"
    />
  </template>
  <template v-else>
    <SFProductCardImageSliderMobile
      :images="images"
      :alt="alt"
      :link="link"
      :is-product-hovered="isProductHovered"
      :product-index="productIndex"
      @click="handleClick"
    />
  </template>
</template>

<script setup lang="ts">
import type { ProductImage as ProductImageType } from '@scayle/storefront-nuxt'
import { computed, ref, useTemplateRef } from 'vue'
import SFProductImage from '../../SFProductImage.vue'
import SFProductCardImageSliderMobile from './SFProductCardImageSliderMobile.vue'
import { PRODUCT_CARD_IMAGE_EAGER_LOAD_SIZE } from '~/constants'
import { SFItemsSlider, SFLink } from '#storefront-ui/components'
import SFSliderArrowButton from '~/modules/ui/runtime/components/core/SFSliderArrowButton.vue'
import { useDefaultBreakpoints } from '#storefront-ui'
import SFSliderDotsIndicator from '#storefront-ui/components/core/SFSliderDotsIndicator.vue'

const { isProductHovered, images, productIndex } = defineProps<{
  link: string
  isProductHovered: boolean
  alt: string
  images: ProductImageType[]
  productIndex: number
}>()

const { smaller } = useDefaultBreakpoints()
const isMobile = smaller('md')
const slider = useTemplateRef('productImageSlider')
const scrollImageIntoView = (
  index: number,
  scrollBehavior: ScrollBehavior = 'auto',
) => {
  slider.value?.scrollImageIntoView(index, scrollBehavior)
}

defineExpose({
  scrollImageIntoView,
})

const currentIndex = ref(0)

const areArrowsShown = computed(() => {
  if (import.meta.server) {
    return false
  }
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  return !isTouchDevice && isProductHovered && images.length > 1
})

const getImageLoading = (index: number) => {
  const shouldEagerLoad = productIndex < PRODUCT_CARD_IMAGE_EAGER_LOAD_SIZE
  return shouldEagerLoad && index === 0 ? 'eager' : 'lazy'
}

const shouldPreload = (index: number) => {
  const shouldPreload = productIndex < PRODUCT_CARD_IMAGE_EAGER_LOAD_SIZE
  return shouldPreload && index === 0
}

const emit = defineEmits<{
  click: []
}>()

const handleClick = () => {
  emit('click')
}
</script>
